import { Document, Image, Link, Page, Text, View } from '@react-pdf/renderer';
import type { ReactNode } from 'react';
import type {
  DocElement,
  ExportDocument,
  InlineContent,
  InlineSpan,
} from 'src/pages/Conversations/document';
import {
  PDF_CONTENT_WIDTH_PT,
  PDF_MAX_IMAGE_HEIGHT_PT,
} from 'src/pages/Conversations/pdf/constants';
import { styles } from './conversation-pdf-export-template.styles';

export interface ConversationPdfExportTemplateProps {
  document: ExportDocument;
  exportDateLabel: string;
  logoDimensions?: { height: number; width: number };
  logoDataUrl?: string | null;
  resolvedImageDimensions?: Record<string, { height: number; width: number }>;
}

interface RenderSectionInput {
  elements: DocElement[];
  resolvedImageDimensions: Record<string, { height: number; width: number }>;
  sectionKey: string;
  showDivider: boolean;
}

const EMPTY_IMAGE_DIMENSIONS: Record<string, { height: number; width: number }> = {};
const LIST_ITEM_INDENT_PT = 14;

const renderInlineContent = (content: InlineContent, keyPrefix: string): ReactNode[] => {
  return content.map((span: InlineSpan, index: number) => {
    const key = `${keyPrefix}-${index}`;

    switch (span.type) {
      case 'text':
        return <Text key={key}>{span.value}</Text>;
      case 'raw':
        return <Text key={key}>{span.value}</Text>;
      case 'bold':
        return (
          <Text key={key} style={styles.strong}>
            {span.value}
          </Text>
        );
      case 'italic':
        return (
          <Text key={key} style={styles.emphasis}>
            {span.value}
          </Text>
        );
      case 'code':
        return (
          <Text key={key} style={styles.inlineCode}>
            {span.value}
          </Text>
        );
      case 'link':
        return (
          <Link key={key} src={span.href} style={styles.link}>
            {span.label}
          </Link>
        );
      default:
        return null;
    }
  });
};

const getHeadingStyle = (
  level: 1 | 2 | 3,
): typeof styles.h1 | typeof styles.h2 | typeof styles.h3 => {
  switch (level) {
    case 1:
      return styles.h1;
    case 2:
      return styles.h2;
    case 3:
      return styles.h3;
  }
};

const renderDocElement = (
  element: DocElement,
  blockKey: string,
  resolvedImageDimensions: Record<string, { height: number; width: number }>,
): ReactNode => {
  switch (element.type) {
    case 'heading':
      return (
        <Text key={blockKey} style={getHeadingStyle(element.level)} orphans={2} widows={2}>
          {renderInlineContent(element.content, blockKey)}
        </Text>
      );
    case 'paragraph':
      return (
        <Text key={blockKey} style={styles.paragraph} orphans={3} widows={2}>
          {renderInlineContent(element.content, blockKey)}
        </Text>
      );
    case 'list-item':
      return (
        <View
          key={blockKey}
          style={[styles.listItem, { marginLeft: element.indent * LIST_ITEM_INDENT_PT }]}
        >
          <Text style={styles.listBullet}>•</Text>
          <Text style={styles.listText}>{renderInlineContent(element.content, blockKey)}</Text>
        </View>
      );
    case 'blockquote':
      return (
        <View key={blockKey} style={styles.blockquote}>
          <Text style={styles.blockquoteText}>
            {renderInlineContent(element.content, blockKey)}
          </Text>
        </View>
      );
    case 'image': {
      const imageDimensions = resolvedImageDimensions[element.src];
      return (
        <Image
          key={blockKey}
          src={element.src}
          style={
            imageDimensions
              ? [
                  styles.image,
                  {
                    height: imageDimensions.height,
                    maxHeight: PDF_MAX_IMAGE_HEIGHT_PT,
                    maxWidth: PDF_CONTENT_WIDTH_PT,
                    width: imageDimensions.width,
                  },
                ]
              : styles.image
          }
        />
      );
    }
    case 'hr':
      return <View key={blockKey} style={styles.sectionDivider} />;
  }
};

const renderSection = ({
  elements,
  resolvedImageDimensions,
  sectionKey,
  showDivider,
}: RenderSectionInput): ReactNode => {
  return (
    <View key={sectionKey} style={styles.section}>
      {elements.map((element, index) => {
        const blockKey = `${sectionKey}-${element.type}-${index}`;
        return renderDocElement(element, blockKey, resolvedImageDimensions);
      })}
      {showDivider ? <View style={styles.sectionDivider} /> : null}
    </View>
  );
};

export const ConversationPdfExportTemplate = ({
  document,
  exportDateLabel,
  logoDimensions,
  logoDataUrl,
  resolvedImageDimensions,
}: ConversationPdfExportTemplateProps): ReactNode => {
  const imageDimensions = resolvedImageDimensions ?? EMPTY_IMAGE_DIMENSIONS;

  return (
    <Document title={document.title}>
      {document.sections.map((section, sectionIndex) => (
        <Page key={section.pageHint} size="LETTER" style={styles.page} wrap>
          {section.pageHint === 'main' ? (
            <View style={styles.header}>
              {logoDataUrl && logoDimensions ? (
                <Image
                  src={logoDataUrl}
                  style={[
                    styles.logo,
                    {
                      height: logoDimensions.height,
                      width: logoDimensions.width,
                    },
                  ]}
                />
              ) : null}
              <Text style={styles.exportDate}>{exportDateLabel}</Text>
            </View>
          ) : null}

          {section.elements.length > 0 ? (
            renderSection({
              elements: section.elements,
              resolvedImageDimensions: imageDimensions,
              sectionKey: `${section.pageHint}-${sectionIndex}`,
              showDivider: false,
            })
          ) : (
            <Text style={styles.emptyState}>
              No conversation content was available for this export.
            </Text>
          )}
        </Page>
      ))}
    </Document>
  );
};
